// manage-response-assign-popup.component.ts

import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { debounceTime, distinctUntilChanged, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { PrimeNGSharedModule } from "../../../../../shared/prime/prime-ng.module";
import { MessageService } from "primeng/api"; // For toast messages, if needed
import { ManageAssessmentApiService } from "../../../../../core/services/api/manage-assessment-api.service";
import { HttpErrorResponse } from "@angular/common/http";
import { Dropdown } from "primeng/dropdown";

@Component({
  selector: "app-manage-response-assign-popup",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PrimeNGSharedModule, // Contains p-dialog, p-button, pInputText, p-input-icon-right
  ],
  templateUrl: "./manage-response-assign-popup.component.html",
  styleUrl: "./manage-response-assign-popup.component.scss",
  providers: [MessageService], // Provide MessageService if you plan to use it for notifications
})
export class ManageResponseAssignPopupComponent implements OnInit, OnDestroy {
  @Input() visible: boolean = false;
  @Input() mappingId: any = null; // You might use this to know which assessment to assign
  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() closeDialog = new EventEmitter<void>();
  @Output() assessmentAssigned = new EventEmitter<any>(); // Emits data when assessment is assigned

  assignForm!: FormGroup;
  private unsubscribe$ = new Subject<void>();
  userOptions: any[] = [];
  private userFilter$ = new Subject<string>();
  @ViewChild("userDropdown") userDropdown!: Dropdown;

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private manageAssessmentApiService: ManageAssessmentApiService,
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.loadDropdownData();
    this.userFilter$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((value) => {
        this.loadDropdownData(value);
      });
  }

  initializeForm(): void {
    this.assignForm = this.fb.group({
      searchUser: ["", Validators.required], // Example: search user is required
    });
  }

  onUserFilter(event: any) {
    const searchText = event.filter;
    this.userFilter$.next(searchText);
  }

  loadDropdownData(searchText: string = "") {
    const obj = {
      country_id: this.mappingId?.country_id,
      ...(searchText && searchText.trim() && { search: searchText.trim() }),
    };
    this.manageAssessmentApiService
      .getResponseAssignUserList(obj)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: any) => {
          this.userOptions = response?.users || [];
          setTimeout(() => {
            if (this.userDropdown?.overlayVisible) {
              this.userDropdown.hide();
              this.userDropdown.show();
            }
          });
        },
        error: (error) => {
          console.error("Error loading user list:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message ||
              "Failed to load user list. Please try again.",
            life: 5000,
          });
        },
      });
  }

  assignAssessment(): void {
    if (this.assignForm.valid) {
      const assignedUser = this.assignForm?.value?.searchUser?.id || null;
      const apiPayload = {
        user_id: assignedUser,
        allow_reassignment: true,
        unassign: false,
      };
      this.manageAssessmentApiService
        .submitRcaResponseToAssignee(
          apiPayload,
          this.mappingId?.response_mapping_id,
        )
        .subscribe({
          next: (response: any) => {
            this.assessmentAssigned.emit({
              mappingId: this.mappingId,
              assignedTo: assignedUser,
              response: response,
            });
            this.close();
            this.messageService.add({
              severity: "success",
              summary: "Success",
              detail: `Response form data assigned successfuly!`,
              life: 5000,
            });
          },
          error: (error: HttpErrorResponse) => {
            console.error("Failed to assign:", error);
            this.messageService.add({
              severity: "error",
              summary: "Error",
              detail:
                error.error?.message || "Failed to assign. Please try again.",
              life: 5000,
            });
          },
        });
    } else {
      this.messageService.add({
        severity: "error",
        summary: "Validation Error",
        detail: "Please enter a user to assign the assessment.",
        life: 3000,
      });
      this.assignForm.markAllAsTouched();
    }
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(this.visible);
    this.closeDialog.emit();
    this.assignForm.reset(); // Reset form when closing
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
